/* Charts the chat response asks for, read from its "CHARTS TO DISPLAY"
   section. The response is the single source of truth — no keyword guessing. */

const CHART_NAME_KEYS = {
  "player capability radar": "radar",
  "capability radar": "radar",
  radar: "radar",
  "transfer value": "transfer",
  transfer: "transfer",
  "formation involvement": "involvementMap",
  "involvement map": "involvementMap",
  finishing: "finishing",
  "chance creation": "chanceCreation",
  passing: "passing",
  "ball control": "ballControl",
  dribbling: "dribbling",
  defending: "defending",
  duels: "duels",
  aerial: "aerial",
  involvement: "involvement",
};

/* Fuzzy fallback so names like "Transfer Value History", "Defensive Actions"
   or "Aerial Duels" still resolve. Order matters (most specific first). */
const NAME_RULES = [
  { key: "radar", test: (n) => n.includes("radar") },
  { key: "transfer", test: (n) => n.includes("transfer") || n.includes("valuation") || n.includes("value") || n.includes("fee") || n.includes("market") },
  { key: "involvementMap", test: (n) => n.includes("formation") || n.includes("involvement map") || n.includes("position") || n.includes("heatmap") },
  { key: "chanceCreation", test: (n) => n.includes("chance") || n.includes("creativ") || n.includes("assist") || n.includes("key pass") },
  { key: "finishing", test: (n) => n.includes("finish") || n.includes("goal") || n.includes("shoot") || n.includes("shot") || n.includes("conversion") },
  { key: "passing", test: (n) => n.includes("pass") || n.includes("distribution") },
  { key: "ballControl", test: (n) => n.includes("ball control") || n.includes("control") || n.includes("retention") || n.includes("touch") },
  { key: "dribbling", test: (n) => n.includes("dribbl") || n.includes("take-on") || n.includes("carries") },
  { key: "defending", test: (n) => n.includes("defen") || n.includes("tackl") || n.includes("interception") || n.includes("clearance") || n.includes("recover") },
  { key: "aerial", test: (n) => n.includes("aerial") || n.includes("header") },
  { key: "duels", test: (n) => n.includes("duel") || n.includes("contest") },
  { key: "involvement", test: (n) => n.includes("involvement") || n.includes("minute") || n.includes("appearance") || n.includes("availability") },
];

const SECTION_HEADING_RE = /^(?:\d+\.\s*)?[A-Z][A-Z0-9 /&.,'()-]{2,}$/;
const NO_CHARTS_RE = /^(none|n\/?a|nil|null|—|–|-)$/i;

function cleanLine(line) {
  return String(line || "").replace(/[*#`_]/g, "").replace(/\s+/g, " ").trim();
}

function keyForName(rawName) {
  const name = String(rawName || "")
    .replace(/\s+/g, " ")
    .replace(/[.:;,]+$/, "")
    .trim()
    .toLowerCase();
  if (!name) return null;
  if (CHART_NAME_KEYS[name]) return CHART_NAME_KEYS[name];
  const rule = NAME_RULES.find((entry) => entry.test(name));
  return rule ? rule.key : null;
}

export function chartsFromResponse(responseText) {
  const lines = String(responseText || "").split("\n");
  let start = -1;
  let headingRest = "";
  for (let i = 0; i < lines.length; i += 1) {
    const match = cleanLine(lines[i]).match(/^(?:\d+\.\s*)?CHARTS TO DISPLAY:?\s*(.*)$/i);
    if (match) {
      start = i + 1;
      headingRest = match[1];
      break;
    }
  }
  if (start === -1) return [];

  const keys = [];
  const addNames = (text) => {
    text
      .replace(/^[-–—•*]+\s*/, "")
      .replace(/^\d+[.)]\s*/, "")
      .split(/[,;]/)
      .map((name) => keyForName(name))
      .filter(Boolean)
      .forEach((key) => {
        if (!keys.includes(key)) keys.push(key);
      });
  };

  if (headingRest && !NO_CHARTS_RE.test(headingRest.trim())) addNames(headingRest);
  for (let i = start; i < lines.length; i += 1) {
    const raw = cleanLine(lines[i]);
    if (!raw || NO_CHARTS_RE.test(raw)) continue;
    if (SECTION_HEADING_RE.test(raw) && !keyForName(raw)) break;
    addNames(raw);
  }
  return keys;
}

/* ---------------------------------------------------------------------------
   Fallback: when the agent doesn't emit a "CHARTS TO DISPLAY" section, derive
   the chart list from the answer/query text, or use a sensible default set.
   --------------------------------------------------------------------------- */

/* Shown when nothing can be inferred. Edit freely — order = nav order. */
export const DEFAULT_CHART_KEYS = [
  "radar",
  "transfer",
  "involvementMap",
  "finishing",
  "chanceCreation",
  "passing",
  "ballControl",
  "dribbling",
  "defending",
  "duels",
  "aerial",
  "involvement",
];

/* Keyword hints used to infer which charts a query/answer is about. Whole-word,
   case-insensitive; order is the order charts are returned. Kept conservative so
   a short question maps to a focused subset rather than everything. */
const INFER_RULES = [
  { key: "radar", test: /\b(radar|capabilit(y|ies)|strength\w*|weakness\w*)\b/i },
  { key: "transfer", test: /\b(transfer\w*|valuation|market value|worth|release clause|fee\w*)\b/i },
  { key: "involvementMap", test: /\b(formation\w*|heatmap|position(s)?)\b/i },
  { key: "finishing", test: /\b(finish\w*|scoring|conversion|goal\w*|shoot\w*)\b/i },
  { key: "chanceCreation", test: /\b(chance\w*|creativ\w*|assist\w*|key pass\w*)\b/i },
  { key: "passing", test: /\b(pass\w*|distribution|pass accuracy)\b/i },
  { key: "ballControl", test: /\b(ball control|touch\w*|retention)\b/i },
  { key: "dribbling", test: /\b(dribbl\w*|take-?on\w*|carri(es|ed))\b/i },
  { key: "defending", test: /\b(defen\w*|tackle\w*|interception\w*|clearance\w*)\b/i },
  { key: "duels", test: /\b(duel\w*|contest\w*)\b/i },
  { key: "aerial", test: /\b(aerial\w*|header\w*)\b/i },
  { key: "involvement", test: /\b(involvement|appearance\w*|availability)\b/i },
];

export function inferChartKeys(text) {
  const source = String(text || "").replace(/\s+/g, " ").toLowerCase();
  if (!source.trim()) return [];
  const keys = [];
  for (const rule of INFER_RULES) {
    if (rule.test.test(source) && !keys.includes(rule.key)) keys.push(rule.key);
  }
  return keys;
}

/* Tiered chart-key resolution:
   1. the explicit CHARTS TO DISPLAY section (still the source of truth);
   2. keyword inference over the answer + query;
   3. the default set — but only when players were resolved (no players → none). */
export function resolveChartKeys(responseText, { query = "", players = [] } = {}) {
  const explicit = chartsFromResponse(responseText);
  if (explicit.length) return explicit;

  const inferred = inferChartKeys(`${responseText || ""}\n${query || ""}`);
  if (inferred.length) return inferred;

  return players && players.length ? DEFAULT_CHART_KEYS.slice() : [];
}
